import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Gift, Save, Loader2, CheckCircle2, RotateCcw, Calculator, Users, TrendingUp, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const BonusesTab = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ threshold: '', per_match_amount: '', notes: '' });
  const [adjDraft, setAdjDraft] = useState({});

  const authConfig = {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bonuses`, { params: { month }, ...authConfig });
      setData(res.data);
      setForm({
        threshold: String(res.data.threshold ?? ''),
        per_match_amount: String(res.data.per_match_amount ?? ''),
        notes: res.data.notes || ''
      });
      setAdjDraft({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Prim verisi alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/bonuses/${month}/config`, {
        threshold: parseInt(form.threshold || '0', 10),
        per_match_amount: parseFloat(form.per_match_amount || '0'),
        notes: form.notes || null
      }, authConfig);
      setData(res.data);
      toast.success('Prim ayarları kaydedildi');
    } catch (e) {
      toast.error('Ayarlar kaydedilemedi');
    } finally { setSaving(false); }
  };

  const patchPerson = async (pid, body, successMsg) => {
    try {
      const res = await axios.put(`${API}/bonuses/${month}/personnel/${pid}`, body, authConfig);
      setData(res.data);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error('Güncellenemedi');
    }
  };

  const toggleIncluded = async (pid) => {
    try {
      const res = await axios.put(`${API}/bonuses/${month}/personnel/${pid}/toggle-included`, {}, authConfig);
      setData(res.data);
    } catch (e) {
      toast.error('Güncellenemedi');
    }
  };

  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="bonuses-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Gift className="w-5 h-5 text-fuchsia-600" /> Primler
          </h2>
          <p className="text-xs text-slate-500">Rezervasyon sayısına göre prim havuzu — kasaya hiçbir kayıt işlenmez</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Ay</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" data-testid="bonus-month-input" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="bonus-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Ayarlar */}
      <Card>
        <CardHeader className="py-3 border-b bg-slate-50/70">
          <CardTitle className="text-base flex items-center gap-2 text-slate-800">
            <Calculator className="w-4 h-4 text-blue-600" /> Prim Kuralı (manuel)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Eşik rezervasyon sayısı</Label>
            <Input type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} data-testid="bonus-threshold-input" />
          </div>
          <div>
            <Label className="text-xs">Maç başı prim (₺)</Label>
            <Input type="number" step="0.01" value={form.per_match_amount} onChange={(e) => setForm({ ...form, per_match_amount: e.target.value })} data-testid="bonus-per-match-input" />
          </div>
          <div>
            <Label className="text-xs">Not (opsiyonel)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="ör. yaz kampanyası" data-testid="bonus-notes-input" />
          </div>
          <Button onClick={saveConfig} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="bonus-save-config-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Kaydet & Hesapla
          </Button>
        </CardContent>
      </Card>

      {/* Hesap özeti */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="bonus-summary">
          <Card className="border-l-4 border-l-blue-500 bg-blue-50">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-slate-500">Ayın Rezervasyonu</p>
              <p className="text-xl font-bold text-blue-700" data-testid="bonus-reservation-count">{data.reservation_count}</p>
              <p className="text-[10px] text-slate-500">eşik: {data.threshold}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500 bg-amber-50">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-slate-500">Eşiği Aşan Maç</p>
              <p className="text-xl font-bold text-amber-700" data-testid="bonus-excess">{data.excess_matches}</p>
              <p className="text-[10px] text-slate-500">× {formatTL(data.per_match_amount)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-fuchsia-500 bg-fuchsia-50">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-slate-500">Prim Havuzu</p>
              <p className="text-xl font-bold text-fuchsia-700" data-testid="bonus-pool">{formatTL(data.pool)}</p>
              <p className="text-[10px] text-slate-500">kalan: {formatTL(data.remainder)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500 bg-emerald-50">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-slate-500">Kişi Başı</p>
              <p className="text-xl font-bold text-emerald-700" data-testid="bonus-per-person">{formatTL(data.per_person)}</p>
              <p className="text-[10px] text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> {data.included_count} personel</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-500 bg-slate-50">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-slate-500">Dağıtılan / Ödenen</p>
              <p className="text-xl font-bold text-slate-800">{formatTL(data.distributed_total)}</p>
              <p className="text-[10px] text-emerald-600">ödenen: {formatTL(data.paid_total)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Personel listesi */}
      <Card>
        <CardHeader className="py-3 border-b bg-slate-50/70">
          <CardTitle className="text-base flex items-center gap-2 text-slate-800">
            <TrendingUp className="w-4 h-4 text-emerald-600" /> Personel Primleri
            <span className="text-xs font-normal text-slate-500">(dahil/çıkar, elle düzeltme ve ödendi işareti)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="py-10 text-center text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Yükleniyor...</div>
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-slate-400 text-sm">Aktif personel bulunmuyor</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white border-b text-xs text-slate-500">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Personel</th>
                    <th className="px-2 py-2 font-medium text-center">Prime Dahil</th>
                    <th className="px-2 py-2 font-medium text-right">Havuzdan</th>
                    <th className="px-2 py-2 font-medium text-right">Elle Düzeltme</th>
                    <th className="px-2 py-2 font-medium text-right">Toplam Prim</th>
                    <th className="px-2 py-2 font-medium text-center">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.personnel_id} className={`border-b ${r.included ? '' : 'bg-slate-50/70 text-slate-400'}`} data-testid={`bonus-row-${r.personnel_id}`}>
                      <td className="px-3 py-2">
                        <p className="font-semibold text-slate-800">{r.name}</p>
                        {r.position && <p className="text-[11px] text-slate-500">{r.position}</p>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Button
                          size="sm"
                          variant={r.included ? 'default' : 'outline'}
                          className={`h-7 text-xs ${r.included ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                          onClick={() => toggleIncluded(r.personnel_id)}
                          data-testid={`bonus-toggle-${r.personnel_id}`}
                        >{r.included ? 'Dahil' : 'Hariç'}</Button>
                      </td>
                      <td className="px-2 py-2 text-right font-mono">{formatTL(r.base_amount)}</td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            type="number"
                            className="h-8 w-24 text-right text-xs"
                            value={adjDraft[r.personnel_id] ?? r.adjustment}
                            onChange={(e) => setAdjDraft({ ...adjDraft, [r.personnel_id]: e.target.value })}
                            data-testid={`bonus-adjust-input-${r.personnel_id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            onClick={() => patchPerson(r.personnel_id, { adjustment: parseFloat(adjDraft[r.personnel_id] ?? r.adjustment) || 0 }, 'Düzeltme kaydedildi')}
                            data-testid={`bonus-adjust-save-${r.personnel_id}`}
                          ><Save className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-slate-900" data-testid={`bonus-total-${r.personnel_id}`}>{formatTL(r.total_amount)}</td>
                      <td className="px-2 py-2 text-center">
                        {r.paid ? (
                          <div className="flex items-center justify-center gap-1">
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Ödendi</Badge>
                            <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => patchPerson(r.personnel_id, { paid: false }, 'Ödeme işareti kaldırıldı')} data-testid={`bonus-unpay-${r.personnel_id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            disabled={!r.included || r.total_amount <= 0}
                            onClick={() => patchPerson(r.personnel_id, { paid: true }, 'Prim ödendi olarak işaretlendi')}
                            data-testid={`bonus-pay-${r.personnel_id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Ödendi İşaretle
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-400 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Hesap: (ayın rezervasyon sayısı − eşik) × maç başı prim = havuz. Havuz, prime dahil personele eşit bölünür ve tam liraya aşağı yuvarlanır;
        bölünemeyen kalan şirkette kalır. Primler kasaya (gelir/gider) <strong>işlenmez</strong>, yalnızca burada takip edilir.
      </p>
    </div>
  );
};

export default BonusesTab;
